import type { MaybeRefOrGetter } from 'vue';
import type { BlockEditorChildBoundsElement } from '#layers/block-engine/utils/block/api/editor-bounds';
import type { BlockBounds } from './bounds';
import { useElementBounding } from '@vueuse/core';
import { computed, onBeforeUnmount, shallowRef, toValue, useId, watch } from 'vue';
import { useBlockEditorContext } from '#layers/block-engine/composables/use-block-editor-context';
import { blockRefKey } from '#layers/block-engine/utils/block/const';
import { getBlockRootElement } from '#layers/block-engine/utils/block/element';

/**
 * Registers reactive child bounds and refreshes them when sibling layout changes.
 *
 * Call within an editor component. An override element takes precedence over the rendered root.
 */
export function useBlockEditorChildBounds(
	child: MaybeRefOrGetter<Pick<BlockBounds, 'childId' | 'slotName' | 'index' | 'horizontal'>>,
	overrideElement?: MaybeRefOrGetter<BlockEditorChildBoundsElement | null>,
) {
	const { bounds } = useBlockEditorContext();
	const blockRef = shallowRef<Record<PropertyKey, any> | null>(null);
	const childMetadata = computed(() => toValue(child));
	const blockRootElement = computed(() => {
		const override = toValue(overrideElement);

		if (override) return override;

		const ref = blockRef.value;

		// Serialized renderers expose a nested blockRef; builtins may expose a root override.
		return getBlockRootElement(
			ref?.[blockRefKey]
			?? ref?.blockRef?.[blockRefKey]
			?? ref?.blockRef
			?? ref,
		);
	});
	const computedBounds = useElementBounding(blockRootElement, { updateTiming: 'next-frame' });
	const boundsId = useId();

	// Direct shallow-ref sources force callbacks even when the metadata fields are unchanged.
	watch([
		() => computedBounds.x.value,
		() => computedBounds.y.value,
		() => computedBounds.width.value,
		() => computedBounds.height.value,
		() => childMetadata.value.childId,
		() => childMetadata.value.slotName,
		() => childMetadata.value.index,
		() => childMetadata.value.horizontal,
	], ([x, y, width, height], [oldX, oldY, oldWidth, oldHeight]) => {
		const metadata = childMetadata.value;

		bounds.bounds.value.set(boundsId, { x, y, width, height, ...metadata });

		if (oldX !== x || oldY !== y || oldWidth !== width || oldHeight !== height) {
			// A size change can shift sibling roots without resizing them.
			bounds.updateEvent.trigger(metadata.childId);
		}
	}, { immediate: true });

	const { off } = bounds.updateEvent.on((id) => {
		if (id !== childMetadata.value.childId) {
			computedBounds.update();
		}
	});

	onBeforeUnmount(() => {
		off();
		bounds.bounds.value.delete(boundsId);
	});

	/** Receives the rendered block instance from its `block:ref` event. */
	function onBlockRef(value: Record<PropertyKey, any> | null) {
		blockRef.value = value;
	}

	return { onBlockRef };
}
